import React from 'react';
import { CHANNELS, Channel, ChannelId } from '../constants/channels';
import { IconRadio } from './icons';
import './SourceSwitch.css';

interface SourceSwitchProps {
  source: ChannelId;
  onChange: (source: ChannelId) => void;
  /** Following only exists once Twitch is connected */
  isLoggedIn?: boolean;
  /** While viewing someone's list the Custom key wears their name */
  sharedLabel?: string | null;
  /** The live catalogue — the built-in constants until the server's arrives */
  channels?: Channel[];
  /** Channel admins get a key on the Crews row that racks a new channel */
  onNewChannel?: () => void;
}

/**
 * The channel dial — a preset rack at the top of the stream list. Row one
 * holds the broad sources (Top 100, Following, Custom), row two the curated
 * crews, row three the Just topics (Gaming, Politics, IRL, RP). One press
 * picks what the sidebar shows, the pool auto-switch jumps inside and, for
 * public channels, the shared room. The tuned key is lit.
 */
export const SourceSwitch: React.FC<SourceSwitchProps> = ({
  source,
  onChange,
  isLoggedIn,
  sharedLabel,
  channels = CHANNELS,
  onNewChannel,
}) => {
  const visible = channels.filter(c => c.id !== 'followed' || isLoggedIn);
  const sources = visible.filter(c => !c.handle);
  const crews = visible.filter(c => !!c.handle && c.tag !== 'just');
  const just = visible.filter(c => c.tag === 'just');

  const keyTitle = (c: Channel): string => {
    if (c.id === 'top') return 'Top 100 — the loudest chats on Twitch, with a shared room';
    if (c.id === 'followed') return 'Channels you follow on Twitch';
    if (c.id === 'custom') {
      return sharedLabel ? `Shared list: ${sharedLabel}` : 'Your own list of streamers';
    }
    if (c.tag === 'just') {
      return `${c.label} — just ${c.label.toLowerCase()}: a curated topic roster, with a shared room`;
    }
    return `${c.label} — curated crew roster, with a shared room`;
  };

  const key = (c: Channel) => {
    const armed = source === c.id;
    const label = c.id === 'custom' && sharedLabel ? sharedLabel : c.label;
    return (
      <button
        key={c.id}
        type="button"
        className={`dial-key ${armed ? 'armed' : ''}`}
        aria-pressed={armed}
        onClick={() => onChange(c.id)}
        title={keyTitle(c)}
      >
        <span className="dial-key-label">{label}</span>
      </button>
    );
  };

  // The admin's key — racks another channel on the dial
  const newKey = onNewChannel ? (
    <button
      key="__new"
      type="button"
      className="dial-key dial-key-new"
      onClick={onNewChannel}
      title="New channel — rack another roster on the dial, for everyone"
      aria-label="New channel"
    >
      <span className="dial-key-label">+</span>
    </button>
  ) : null;

  const row = (rail: string, keys: Channel[], extra: React.ReactNode = null) => (
    <div
      className={`dial-row ${extra ? 'has-new' : ''}`}
      role="group"
      aria-label={rail}
      style={{ '--keys': keys.length } as React.CSSProperties}
    >
      <span className="dial-rail" aria-hidden>{rail}</span>
      {keys.map(key)}
      {extra}
    </div>
  );

  return (
    <section className="channel-dial" aria-label="Change channel">
      <div className="dial-caption">
        <IconRadio size={12} className="dial-caption-icon" />
        <span>Change channel</span>
      </div>
      <div className="dial-rack">
        {row('Source', sources)}
        {row('Crews', crews, newKey)}
        {row('Just', just)}
      </div>
    </section>
  );
};
